// pages/analysis/HealthAssetDetail.js —— 分析中心 · 健康度分析 · 资产详情（二级页面，doc01 2.14 操作说明）
// 「详情」进入本页：查看单个资产当年 4 个指标（资产寿命 / 运行效率 / 安全性 / 可靠性）的实测值与预测值对比曲线。
// 4 条对比曲线分 4 个独立区块呈现；预测值未配置的资产显示无健康度分析结果占位。
import React from 'react';
import { Button } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { HEALTH_ASSETS, getHealthTrend } from '../../config/stationData';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TIP = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };

// 单个指标区块：当年实测（实线）vs 预测（虚线）
const MetricChart = ({ title, data, actualKey, predKey, color }) => (
  <div className="panel">
    <div className="panel-title">{title}<small>实测 vs 预测 · 单位 %</small></div>
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 10, right: 12, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
        <XAxis dataKey="month" tick={TICK} axisLine={false} tickLine={false} />
        <YAxis tick={TICK} axisLine={false} tickLine={false} width={48} domain={['dataMin - 1', 'dataMax + 1']} />
        <Tooltip contentStyle={TIP} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
        <Line type="monotone" dataKey={actualKey} name="实测值" stroke={color} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey={predKey} name="预测值" stroke={color} dot={false} strokeWidth={2} strokeDasharray="5 4" />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const InfoRow = ({ items }) => (
  <div className="dev-kv">
    {items.map(([k, v]) => (
      <div className="dev-kv-row" key={k}>
        <span className="dev-kv-k">{k}</span>
        <span className="dev-kv-v">{v}</span>
      </div>
    ))}
  </div>
);

const HealthAssetDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const asset = HEALTH_ASSETS.find((a) => String(a.key) === String(id));

  const back = (
    <div style={{ marginBottom: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/analysis/health')}>返回</Button>
    </div>
  );

  if (!asset) {
    return (
      <div>
        {back}
        <div className="panel">
          <div className="panel-title">资产健康度详情</div>
          <div style={{ color: 'rgba(255,255,255,0.55)' }}>未找到对应资产（编号：{id}）。</div>
        </div>
      </div>
    );
  }

  const configured = typeof asset.life === 'number';
  const trend = getHealthTrend();

  return (
    <div>
      {back}

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-title">资产健康度详情<small>{asset.name} 当年 4 个指标实测值 vs 预测值</small></div>
        <InfoRow items={[
          ['站点名称', asset.site],
          ['资产编号', asset.code],
          ['资产名称', asset.name],
          ['出厂日期', asset.factory],
          ['是否配置预测值', asset.predictCfg],
        ]} />
      </div>

      {configured ? (
        <div className="grid-2-even">
          <MetricChart title="资产寿命" data={trend} actualKey="lifeA" predKey="lifeP" color="#00d4ff" />
          <MetricChart title="运行效率" data={trend} actualKey="effA" predKey="effP" color="#52e8b0" />
          <MetricChart title="安全性指标" data={trend} actualKey="safetyA" predKey="safetyP" color="#f4c542" />
          <MetricChart title="可靠性指标" data={trend} actualKey="reliA" predKey="reliP" color="#8b7cf6" />
        </div>
      ) : (
        <div className="panel">
          <div className="panel-title">健康度分析结果</div>
          <div style={{ color: 'rgba(255,255,255,0.55)' }}>
            该资产未配置预测值或未关联站点，无健康度分析结果。
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthAssetDetail;
